import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { Loader2, ArrowLeft, Edit, Download, Trash2, FileText, CheckCircle, XCircle, ArrowRight, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-800' },
  in_progress: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Concluído', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800' },
};

const RHProcessDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProcesso();
  }, [id]);

  const loadProcesso = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rh/processos/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar processo');
      navigate('/rh/admissoes');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rh/processos/${id}/documentos/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const handleConcluirDocumentosManual = async () => {
    const motivo = window.prompt('Documentos foram tratados fora do fluxo de solicitação (ex.: Documentos RH / Assinatura Eletrônica)?\nDescreva o motivo para marcar esta etapa como concluída manualmente:');
    if (motivo === null) return;
    if (!motivo.trim()) {
      toast.error('Informe o motivo da conclusão manual');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/rh/processos/${id}/documentos/concluir-manual`, { motivo }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Etapa de documentos marcada como concluída');
      loadProcesso();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir documentos manualmente');
    }
  };

  const handleRemoveDoc = async (docId) => {
    if (!window.confirm('Remover este documento?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/rh/processos/${id}/documentos/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Documento removido');
      loadProcesso();
    } catch (error) {
      toast.error('Erro ao remover documento');
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  if (!data) return null;

  const { processo, fields, required_documents, attached_documents } = data;
  const statusConf = STATUS_CONFIG[processo.status] || { label: processo.status, color: 'bg-gray-100 text-gray-800' };
  const isEditable = processo.status !== 'completed' && processo.status !== 'cancelled';
  const missingRequiredFields = fields.filter(f => f.required && !processo.fields_data?.[f.field_key]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(processo.process_type_slug === 'admissao' ? '/rh/admissoes' : '/rh/desligamentos')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {processo.process_type_slug === 'admissao' ? 'Admissão' : 'Desligamento'}
            </h1>
            <p className="text-sm text-gray-500">
              Regime: {processo.contract_type?.toUpperCase()} | Status: <Badge className={statusConf.color}>{statusConf.label}</Badge>
            </p>
          </div>
        </div>
        {isEditable && (
          <div className="flex items-center gap-2">
            {processo.process_type_slug === 'admissao' && processo.etapa_documentos_concluida && (
              <Button
                variant="outline"
                onClick={() => navigate(`/rh/processos/${id}/exame-admissional`)}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                {processo.exame_admissional?.status === 'concluido' ? 'Finalizar Admissão' : 'Configurar Exame Admissional'}
              </Button>
            )}
            {processo.process_type_slug === 'admissao' && !processo.etapa_documentos_concluida && (
              <Button variant="outline" onClick={handleConcluirDocumentosManual} title="Use quando os documentos foram tratados fora do fluxo de solicitação (ex.: Documentos RH / Assinatura Eletrônica)">
                <CheckCircle className="h-4 w-4 mr-2" />
                Marcar Documentos como Concluídos
              </Button>
            )}
            <Button onClick={() => navigate(`/rh/processos/${id}/editar`)}>
              <Edit className="h-4 w-4 mr-2" /> Editar
            </Button>
          </div>
        )}
      </div>

      {isEditable && missingRequiredFields.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900">Campos obrigatórios pendentes</AlertTitle>
          <AlertDescription className="text-amber-800">
            <p>Preencha antes de concluir a admissão: {missingRequiredFields.map(f => f.field_label).join(', ')}.</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate(`/rh/processos/${id}/editar`)}>
              <Edit className="h-4 w-4 mr-2" /> Preencher agora
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {processo.employee_id && (
        <Card>
          <CardHeader><CardTitle>Funcionário</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm">
              {processo.employee_nome || 'Não informado'}
              {processo.status === 'completed' && processo.process_type_slug === 'admissao' && (
                <Badge className="ml-2 bg-green-100 text-green-800">Criado na conclusão</Badge>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Dados do Processo</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.filter(f => processo.fields_data?.[f.field_key]).map(field => (
              <div key={field.field_key}>
                <span className="text-sm text-gray-500">{field.field_label}</span>
                <p className="font-medium">{processo.fields_data[field.field_key]}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Documentos</CardTitle>
          <CardDescription>{attached_documents?.length || 0} de {required_documents?.length || 0} documentos anexados</CardDescription>
          {processo.documentos_concluidos_manual && (
            <Badge className="bg-amber-100 text-amber-800 w-fit mt-1">
              Concluído manualmente por {processo.documentos_concluidos_manual.por_nome} — {processo.documentos_concluidos_manual.motivo}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {required_documents?.map(doc => {
            const attached = attached_documents?.find(a => a.nome === doc.nome);
            return (
              <div key={doc.id || doc.nome} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{doc.nome}</span>
                  {doc.obrigatorio && <Badge variant="outline" className="text-xs">Obrigatório</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  {attached ? (
                    <>
                      <Badge className="bg-green-100 text-green-800">Anexado</Badge>
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(attached)} title="Baixar">
                        <Download className="h-4 w-4" />
                      </Button>
                      {isEditable && (
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveDoc(attached.id)} title="Remover">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800">Pendente</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default RHProcessDetailPage;
